import { useEffect, useState } from "react";
import getSearchCategories from "../helpers/getSearchCategories";
import { SelectOptionT } from "../../../models/models";
import { useAppSelector } from "../../../store/hooks";
import { selectCurrentUser } from "../../../store/selectors/authSelectors";
import useQueryParams from "../../../utils/hooks/useQueryParams";
import { useLocation } from "react-router";
import getLastValue from "../../Search/helpers/getLastValue";
import { RoutesE } from "../../../routes/routesData";
import { DEFAULT_DELIVERIES_SEARCH_CATEGORY } from "../../../utils/constants";
import { ActionMeta } from "react-select";

const CATEGORY_QUERY_PARAM_NAME = "filterType";

const useCategory = (isForCampaignsPage: boolean) => {
  const user = useAppSelector(selectCurrentUser);
  const categories = getSearchCategories(isForCampaignsPage, user?.role);
  const [category, setCategory] = useState<SelectOptionT>(null);
  const { searchParams } = useQueryParams();
  const location = useLocation();

  useEffect(() => {
    if (!category && categories?.length) {
      const categoryQueryParam = searchParams.get(CATEGORY_QUERY_PARAM_NAME);
      // set category from query params
      if (categoryQueryParam) {
        const index = categories.findIndex(
          ({ value }) => value === categoryQueryParam
        );
        setCategory(categories[index]);
        // set last selected category from locale storage
      } else {
        const isCampaignsPage = location.pathname === RoutesE.CAMPAIGNS;
        const lastValue = getLastValue(isCampaignsPage);

        /**
         * The remembered choice, else the page's own default. `.find` rather than the old
         * `categories[findIndex(...)]`: a remembered value can name a category this user's role
         * does not get (`categoryOptions` filters by role and localStorage outlives a role
         * change), and `categories[-1]` is `undefined`, which used to leave the control blank.
         * Now that miss falls through to the default below.
         */
        const remembered = categories.find(({ value }) => value === lastValue);

        /**
         * Only the DELIVERIES list has a default; the campaigns list deliberately starts empty so
         * that its "Search in..." placeholder is what the user sees.
         *
         * Keyed on the PATHNAME, not on `isForCampaignsPage`: CampaignList renders `<Search
         * withAutocomplete />` without `isCampaignList`, so that prop arrives `undefined` on
         * /campaigns and would hand the campaigns page the deliveries default. The pathname is
         * what this hook already trusts for `getLastValue` one line above.
         *
         * Resolved against `categories` rather than built here, so it can never select an option
         * this role was not offered.
         */
        const fallback = isCampaignsPage
          ? undefined
          : categories.find(
              ({ value }) => value === DEFAULT_DELIVERIES_SEARCH_CATEGORY
            );

        const resolved = remembered || fallback;

        if (resolved) setCategory(resolved);
      }
    }
  }, []);

  const selectCategory = (
    selectedCategory: SelectOptionT,
    { action }: ActionMeta<unknown>
  ) => {
    if (action === "select-option") {
      setCategory(selectedCategory);
    }
  };

  return {
    categories,
    selectedCategory: category,
    selectCategory,
  };
};

export default useCategory;
